import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useId,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FeedComposer from '../components/FeedComposer';
import FeedPostCard from '../components/FeedPostCard';
import FeedSkeletonList from '../components/FeedSkeletonList';
import useFeedTimeline from '../hooks/useFeedTimeline';
import { useToast } from '../context/ToastProvider';
import './FeedScreen.css';
import { localizedValue } from '../localization/presentationLanguage';
import { registerBackHandler } from '../platform/navigation';
import { getAuthToken } from '../utils/auth';
const feedText = (es, en) => localizedValue({ es, en });

const mobileSheetBackdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 120,
  background:
    'radial-gradient(640px 240px at 50% 100%, rgba(255,215,0,0.10), rgba(255,215,0,0) 70%), rgba(7,4,16,0.58)',
  backdropFilter: 'blur(10px) saturate(118%)',
  WebkitBackdropFilter: 'blur(10px) saturate(118%)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: '0 10px calc(env(safe-area-inset-bottom, 0px) + 10px)',
  overscrollBehavior: 'contain',
  willChange: 'background, backdrop-filter, -webkit-backdrop-filter, opacity',
};

const mobileSheetStyle = {
  width: '100%',
  maxWidth: 760,
  borderRadius: '24px 24px 22px 22px',
  border: '1px solid rgba(255,224,130,0.16)',
  background:
    'linear-gradient(180deg, rgba(40,20,58,0.985) 0%, rgba(28,13,43,0.985) 54%, rgba(20,9,31,0.992) 100%)',
  boxShadow:
    '0 34px 90px rgba(0,0,0,0.44), 0 0 0 1px rgba(255,255,255,0.035) inset, 0 0 48px rgba(255,224,130,0.10)',
  overflow: 'hidden',
  transformOrigin: 'center bottom',
  willChange: 'transform, opacity, box-shadow',
};

const mobileSheetHandleStyle = {
  width: 42,
  height: 5,
  borderRadius: 999,
  background:
    'linear-gradient(90deg, rgba(255,255,255,0.28), rgba(255,224,130,0.58), rgba(255,255,255,0.28))',
  boxShadow: '0 0 12px rgba(255,224,130,0.14)',
  margin: '0 auto',
  opacity: 0.96,
  transformOrigin: 'center center',
  willChange: 'transform, opacity, box-shadow',
};

function getStoredViewerSnapshot() {
  const candidates = [
    localStorage.getItem('user'),
    localStorage.getItem('authUser'),
    localStorage.getItem('zodiacus_user'),
    sessionStorage.getItem('user'),
    sessionStorage.getItem('authUser'),
    sessionStorage.getItem('zodiacus_user'),
  ].filter(Boolean);

  for (const raw of candidates) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      // ignore invalid stored payloads
    }
  }

  return null;
}

function useFeedModeIndicator({ feedModeTabsRef, feedMode, optionsLength, enabled = true }) {
  const indicatorFrameRef = useRef(0);
  const [feedModeIndicatorStyle, setFeedModeIndicatorStyle] = useState({
    width: 0,
    x: 0,
    opacity: 0,
  });

  useLayoutEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const updateFeedModeIndicator = () => {
      const tabsNode = feedModeTabsRef.current;
      if (!tabsNode) return;

      const activeNode = tabsNode.querySelector('[data-feed-mode-active="1"]');
      if (!activeNode) {
        setFeedModeIndicatorStyle((prev) => {
          if (prev.opacity === 0 && prev.width === 0) return prev;
          return { width: 0, x: 0, opacity: 0 };
        });
        return;
      }

      const tabsRect = tabsNode.getBoundingClientRect();
      const activeRect = activeNode.getBoundingClientRect();
      const nextWidth = Math.max(28, Math.round(activeRect.width));
      const nextX = Math.round(activeRect.left - tabsRect.left);

      setFeedModeIndicatorStyle((prev) => {
        if (
          prev.width === nextWidth &&
          prev.x === nextX &&
          prev.opacity === 1
        ) {
          return prev;
        }

        return {
          width: nextWidth,
          x: nextX,
          opacity: 1,
        };
      });
    };

    const scheduleUpdate = () => {
      if (indicatorFrameRef.current) {
        window.cancelAnimationFrame(indicatorFrameRef.current);
      }
      indicatorFrameRef.current = window.requestAnimationFrame(() => {
        indicatorFrameRef.current = 0;
        updateFeedModeIndicator();
      });
    };

    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('resize', scheduleUpdate);
      if (indicatorFrameRef.current) {
        window.cancelAnimationFrame(indicatorFrameRef.current);
        indicatorFrameRef.current = 0;
      }
    };
  }, [enabled, feedMode, feedModeTabsRef, optionsLength]);

  return feedModeIndicatorStyle;
}

function useFeedInfiniteScroll({ disabled, onLoadMore }) {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target || disabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        onLoadMore();
      },
      {
        root: null,
        rootMargin: '240px 0px 320px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(target);

    return () => {
      try {
        observer.disconnect();
      } catch {}
    };
  }, [disabled, onLoadMore]);

  return targetRef;
}

function useMobileComposerSheet({ isMobile, composerSectionRef }) {
  const composerSheetRef = useRef(null);
  const composerSheetOverlayRef = useRef(null);
  const composerSheetHandleRef = useRef(null);
  const mobileSheetPointerStartYRef = useRef(0);
  const mobileSheetPointerDeltaYRef = useRef(0);
  const mobileSheetDragStartTsRef = useRef(0);
  const mobileSheetDraggingRef = useRef(false);
  const closeComposerTimeoutRef = useRef(null);
  const mobileComposerOpenRafRef = useRef(0);
  const mobileSheetTitleId = useId();
  const mobileSheetDescriptionId = useId();

  const [mobileComposerOpen, setMobileComposerOpen] = useState(false);
  const [mobileComposerMounted, setMobileComposerMounted] = useState(false);
  const [mobileComposerClosing, setMobileComposerClosing] = useState(false);

  const resetComposerSheetDragVisual = useCallback(() => {
    const sheetNode = composerSheetRef.current;
    const overlayNode = composerSheetOverlayRef.current;
    const handleNode = composerSheetHandleRef.current;

    if (sheetNode) {
      sheetNode.style.transition = '';
      sheetNode.style.transform = 'translate3d(0, 0, 0) scale(1)';
      sheetNode.style.opacity = '1';
      sheetNode.style.boxShadow = mobileSheetStyle.boxShadow;
    }

    if (overlayNode) {
      overlayNode.style.transition = '';
      overlayNode.style.background = mobileSheetBackdropStyle.background;
      overlayNode.style.backdropFilter = mobileSheetBackdropStyle.backdropFilter;
      overlayNode.style.WebkitBackdropFilter = mobileSheetBackdropStyle.WebkitBackdropFilter;
      overlayNode.style.opacity = '1';
    }

    if (handleNode) {
      handleNode.style.transition = '';
      handleNode.style.transform = 'translate3d(0, 0, 0) scaleX(1)';
      handleNode.style.opacity = '0.96';
      handleNode.style.boxShadow = mobileSheetHandleStyle.boxShadow;
    }
  }, []);

  const applyComposerSheetDragVisual = useCallback((deltaY) => {
    const sheetNode = composerSheetRef.current;
    const overlayNode = composerSheetOverlayRef.current;
    const handleNode = composerSheetHandleRef.current;
    if (!sheetNode) return;

    const damped = Math.min(Math.max(deltaY, 0), 260);
    const progress = Math.min(damped / 220, 1);
    const scale = 1 - (progress * 0.024);
    const opacity = 1 - (progress * 0.22);
    const glowOpacity = Math.max(0.04, 0.1 - progress * 0.06);

    sheetNode.style.transform = `translate3d(0, ${damped}px, 0) scale(${scale})`;
    sheetNode.style.opacity = String(opacity);
    sheetNode.style.boxShadow = `0 30px 78px rgba(0,0,0,${0.44 - progress * 0.16}), 0 0 0 1px rgba(255,255,255,0.035) inset, 0 0 48px rgba(255,224,130,${glowOpacity})`;

    if (overlayNode) {
      const veil = Math.max(0.18, 0.58 - progress * 0.34);
      const blur = Math.max(4.5, 10 - progress * 4.8);
      const glow = Math.max(0.02, 0.1 - progress * 0.06);
      overlayNode.style.background = `radial-gradient(640px 240px at 50% 100%, rgba(255,215,0,${glow}), rgba(255,215,0,0) 70%), rgba(7,4,16,${veil})`;
      overlayNode.style.backdropFilter = `blur(${blur}px) saturate(${118 - progress * 12}%)`;
      overlayNode.style.WebkitBackdropFilter = `blur(${blur}px) saturate(${118 - progress * 12}%)`;
      overlayNode.style.opacity = String(1 - progress * 0.08);
    }

    if (handleNode) {
      handleNode.style.transform = `translate3d(0, ${Math.min(damped * 0.16, 12)}px, 0) scaleX(${1 + progress * 0.16})`;
      handleNode.style.opacity = String(0.96 - progress * 0.18);
      handleNode.style.boxShadow = `0 0 ${12 + progress * 8}px rgba(255,224,130,${0.14 + progress * 0.12})`;
    }
  }, []);

  const openMobileComposer = useCallback(() => {
    if (!isMobile) {
      setMobileComposerOpen((prev) => !prev);
      return;
    }

    if (closeComposerTimeoutRef.current) {
      window.clearTimeout(closeComposerTimeoutRef.current);
      closeComposerTimeoutRef.current = null;
    }

    if (mobileComposerOpenRafRef.current) {
      window.cancelAnimationFrame(mobileComposerOpenRafRef.current);
      mobileComposerOpenRafRef.current = 0;
    }

    setMobileComposerClosing(false);
    setMobileComposerMounted(true);

    mobileComposerOpenRafRef.current = window.requestAnimationFrame(() => {
      mobileComposerOpenRafRef.current = 0;
      if (!isMobile) return;
      setMobileComposerOpen(true);
    });
  }, [isMobile]);

  const closeMobileComposer = useCallback(() => {
    if (mobileComposerOpenRafRef.current) {
      window.cancelAnimationFrame(mobileComposerOpenRafRef.current);
      mobileComposerOpenRafRef.current = 0;
    }

    if (!isMobile) {
      setMobileComposerOpen(false);
      return;
    }

    setMobileComposerClosing(true);
    setMobileComposerOpen(false);

    if (closeComposerTimeoutRef.current) {
      window.clearTimeout(closeComposerTimeoutRef.current);
    }

    closeComposerTimeoutRef.current = window.setTimeout(() => {
      setMobileComposerMounted(false);
      setMobileComposerClosing(false);
      closeComposerTimeoutRef.current = null;
    }, 220);
  }, [isMobile]);

  useEffect(() => {
    if (!mobileComposerMounted || mobileComposerClosing) return undefined;
    return registerBackHandler(() => {
      closeMobileComposer();
      return true;
    });
  }, [closeMobileComposer, mobileComposerClosing, mobileComposerMounted]);

  const handleToggleMobileComposer = useCallback(() => {
    const nextOpen = !(
      isMobile ? mobileComposerMounted && mobileComposerOpen : mobileComposerOpen
    );

    if (nextOpen) {
      openMobileComposer();
      window.setTimeout(() => {
        try {
          composerSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        } catch {}
      }, 40);
      return;
    }

    closeMobileComposer();
  }, [
    closeMobileComposer,
    composerSectionRef,
    isMobile,
    mobileComposerMounted,
    mobileComposerOpen,
    openMobileComposer,
  ]);

  const handleComposerSheetPointerDown = useCallback(
    (event) => {
      if (!isMobile) return;
      if (event.pointerType === 'mouse') return;

      const targetTag = String(event.target?.tagName || '').toLowerCase();
      if (targetTag === 'textarea' || targetTag === 'input' || targetTag === 'button') {
        return;
      }

      const dragHandle = event.target?.closest?.('[data-feed-sheet-drag-handle="1"]');
      if (!dragHandle) return;

      mobileSheetDraggingRef.current = true;
      mobileSheetPointerStartYRef.current = event.clientY || 0;
      mobileSheetPointerDeltaYRef.current = 0;
      mobileSheetDragStartTsRef.current = Date.now();

      if (composerSheetRef.current) {
        composerSheetRef.current.style.transition = 'none';
      }
      if (composerSheetOverlayRef.current) {
        composerSheetOverlayRef.current.style.transition = 'none';
      }
      if (composerSheetHandleRef.current) {
        composerSheetHandleRef.current.style.transition = 'none';
      }

      if (composerSheetRef.current?.setPointerCapture) {
        try {
          composerSheetRef.current.setPointerCapture(event.pointerId);
        } catch {}
      }
    },
    [isMobile]
  );

  const handleComposerSheetPointerMove = useCallback(
    (event) => {
      if (!mobileSheetDraggingRef.current) return;

      const rawDeltaY = Math.max(
        0,
        (event.clientY || 0) - mobileSheetPointerStartYRef.current
      );
      const softened = rawDeltaY <= 120 ? rawDeltaY : 120 + (rawDeltaY - 120) * 0.42;

      mobileSheetPointerDeltaYRef.current = softened;
      applyComposerSheetDragVisual(softened);
    },
    [applyComposerSheetDragVisual]
  );

  const handleComposerSheetPointerEnd = useCallback(
    (event) => {
      if (!mobileSheetDraggingRef.current) return;
      mobileSheetDraggingRef.current = false;

      if (composerSheetRef.current?.releasePointerCapture && event?.pointerId != null) {
        try {
          composerSheetRef.current.releasePointerCapture(event.pointerId);
        } catch {}
      }

      const sheetNode = composerSheetRef.current;
      const overlayNode = composerSheetOverlayRef.current;
      const handleNode = composerSheetHandleRef.current;
      const deltaY = mobileSheetPointerDeltaYRef.current;
      const elapsed = Math.max(1, Date.now() - mobileSheetDragStartTsRef.current);
      const velocity = deltaY / elapsed;

      mobileSheetPointerDeltaYRef.current = 0;
      mobileSheetDragStartTsRef.current = 0;

      if (!sheetNode) return;

      const shouldDismiss = deltaY > 112 || (deltaY > 54 && velocity > 0.72);

      if (shouldDismiss) {
        sheetNode.style.transition = 'transform 180ms cubic-bezier(.22,.88,.24,1), opacity 180ms ease, box-shadow 180ms ease';
        sheetNode.style.transform = 'translate3d(0, 220px, 0) scale(0.972)';
        sheetNode.style.opacity = '0';
        sheetNode.style.boxShadow = '0 18px 42px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.02) inset, 0 0 22px rgba(255,224,130,0.02)';

        if (overlayNode) {
          overlayNode.style.transition = 'background 180ms ease, backdrop-filter 180ms ease, -webkit-backdrop-filter 180ms ease, opacity 180ms ease';
          overlayNode.style.background = 'radial-gradient(640px 240px at 50% 100%, rgba(255,215,0,0.02), rgba(255,215,0,0) 70%), rgba(7,4,16,0.12)';
          overlayNode.style.backdropFilter = 'blur(3px) saturate(102%)';
          overlayNode.style.WebkitBackdropFilter = 'blur(3px) saturate(102%)';
          overlayNode.style.opacity = '0';
        }

        if (handleNode) {
          handleNode.style.transition = 'transform 180ms ease, opacity 180ms ease, box-shadow 180ms ease';
          handleNode.style.transform = 'translate3d(0, 14px, 0) scaleX(1.18)';
          handleNode.style.opacity = '0';
          handleNode.style.boxShadow = '0 0 4px rgba(255,224,130,0.04)';
        }

        window.setTimeout(() => {
          closeMobileComposer();
        }, 150);
        return;
      }

      sheetNode.style.transition = 'transform 260ms cubic-bezier(.18,.9,.2,1.08), opacity 220ms ease, box-shadow 220ms ease';
      if (overlayNode) {
        overlayNode.style.transition = 'background 220ms ease, backdrop-filter 220ms ease, -webkit-backdrop-filter 220ms ease, opacity 220ms ease';
      }
      if (handleNode) {
        handleNode.style.transition = 'transform 240ms cubic-bezier(.18,.9,.2,1.06), opacity 220ms ease, box-shadow 220ms ease';
      }

      resetComposerSheetDragVisual();

      window.setTimeout(() => {
        resetComposerSheetDragVisual();
      }, 260);
    },
    [closeMobileComposer, resetComposerSheetDragVisual]
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!isMobile || !mobileComposerOpen) return undefined;

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyTouchAction = body.style.touchAction;
    const previousBodyOverscroll = body.style.overscrollBehavior;
    const previousHtmlOverflow = documentElement.style.overflow;
    const previousHtmlOverscroll = documentElement.style.overscrollBehavior;

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'none';
    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';

    return () => {
      body.style.overflow = previousBodyOverflow;
      body.style.touchAction = previousBodyTouchAction;
      body.style.overscrollBehavior = previousBodyOverscroll;
      documentElement.style.overflow = previousHtmlOverflow;
      documentElement.style.overscrollBehavior = previousHtmlOverscroll;
    };
  }, [isMobile, mobileComposerOpen]);

  useEffect(() => {
    return () => {
      if (closeComposerTimeoutRef.current) {
        window.clearTimeout(closeComposerTimeoutRef.current);
      }
      if (mobileComposerOpenRafRef.current) {
        window.cancelAnimationFrame(mobileComposerOpenRafRef.current);
        mobileComposerOpenRafRef.current = 0;
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobile || !mobileComposerMounted) return undefined;

    const previousActive = document.activeElement;
    const focusTimer = window.setTimeout(() => {
      const target = composerSheetRef.current?.querySelector(
        'textarea, [contenteditable="true"], input'
      );
      if (target?.focus) {
        try {
          target.focus({ preventScroll: true });
        } catch {
          target.focus();
        }
      }
    }, mobileComposerOpen ? 180 : 0);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobileComposer();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      if (previousActive && typeof previousActive.focus === 'function') {
        window.setTimeout(() => {
          try {
            previousActive.focus({ preventScroll: true });
          } catch {
            previousActive.focus();
          }
        }, 0);
      }
    };
  }, [closeMobileComposer, isMobile, mobileComposerMounted, mobileComposerOpen]);

  useEffect(() => {
    if (!isMobile || !mobileComposerMounted) return undefined;

    const handleWindowResize = () => {
      resetComposerSheetDragVisual();
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [isMobile, mobileComposerMounted, resetComposerSheetDragVisual]);

  useEffect(() => {
    if (!isMobile || !mobileComposerMounted) return undefined;

    const timer = window.setTimeout(() => {
      resetComposerSheetDragVisual();
    }, mobileComposerOpen ? 24 : 0);

    return () => window.clearTimeout(timer);
  }, [isMobile, mobileComposerMounted, mobileComposerOpen, resetComposerSheetDragVisual]);

  return {
    mobileComposerOpen,
    mobileComposerMounted,
    mobileComposerClosing,
    mobileSheetTitleId,
    mobileSheetDescriptionId,
    composerSheetRef,
    composerSheetOverlayRef,
    composerSheetHandleRef,
    openMobileComposer,
    closeMobileComposer,
    handleToggleMobileComposer,
    handleComposerSheetPointerDown,
    handleComposerSheetPointerMove,
    handleComposerSheetPointerEnd,
  };
}

function FeedModeBar({
  feedMode,
  setFeedMode,
  feedModeOptions,
  feedModeTabsRef,
  feedModeIndicatorStyle,
  isMobile,
  refreshing,
  checkingNewPosts,
}) {
  return (
    <div
      className="feed-screen__panel"
      style={{
        padding: isMobile ? '8px 0 4px' : '4px 0 0',
        borderRadius: isMobile ? 16 : 0,
        backdropFilter: isMobile ? 'blur(14px)' : 'none',
        WebkitBackdropFilter: isMobile ? 'blur(14px)' : 'none',
        background: isMobile
          ? 'linear-gradient(180deg, rgba(30,12,45,0.82), rgba(20,8,31,0.58))'
          : 'transparent',
        border: 'none',
        boxShadow: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: isMobile ? 6 : 12,
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 220px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: refreshing || checkingNewPosts ? '#FFD700' : '#B78CFF',
                boxShadow: refreshing || checkingNewPosts
                  ? '0 0 10px rgba(255,215,0,0.50)'
                  : '0 0 10px rgba(183,140,255,0.42)',
                animation: refreshing || checkingNewPosts
                  ? 'feed-live-dot-pulse 1.25s ease-in-out infinite'
                  : 'none',
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: 'rgba(255,255,255,0.75)',
              }}
            >
              {refreshing || checkingNewPosts ? 'Sync' : 'Ahora'}
            </span>
          </div>
        </div>

        <div
          ref={feedModeTabsRef}
          className="feed-screen__feed-mode-tabs"
          style={{
            position: 'relative',
            display: 'flex',
            gap: isMobile ? 14 : 18,
            overflowX: 'auto',
            paddingTop: 0,
            paddingBottom: isMobile ? 8 : 10,
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            width: '100%',
          }}
        >
          <span
            aria-hidden="true"
            className="feed-screen__feed-mode-indicator"
            style={{
              width: feedModeIndicatorStyle.width,
              transform: `translate3d(${feedModeIndicatorStyle.x}px, 0, 0)`,
              opacity: feedModeIndicatorStyle.opacity,
            }}
          />

          {feedModeOptions.map((mode) => {
            const active = feedMode === mode.key;
            return (
              <button
                key={mode.key}
                type="button"
                data-feed-mode-active={active ? '1' : '0'}
                onClick={() => setFeedMode(mode.key)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '0 0 6px',
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: active ? 900 : 700,
                  color: active ? '#FFF3C4' : 'rgba(255,255,255,0.52)',
                  cursor: 'pointer',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  transition: 'color 180ms ease, transform 180ms ease, opacity 180ms ease',
                  transform: active ? 'translateY(-1px)' : 'translateY(0)',
                  opacity: active ? 1 : 0.9,
                  zIndex: 1,
                }}
                title={mode.hint}
                aria-pressed={active}
              >
                <span
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {mode.label}
                  {active ? (
                    <span
                      aria-hidden="true"
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: 999,
                        background: '#FFD700',
                        boxShadow: '0 0 10px rgba(255,215,0,0.42)',
                        animation: 'feed-live-dot-pulse 1.5s ease-in-out infinite',
                      }}
                    />
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FeedMobileSheet({
  open,
  closing,
  titleId,
  descriptionId,
  onClose,
  onSubmit,
  creatingPost,
  composerSheetRef,
  composerSheetOverlayRef,
  composerSheetHandleRef,
}) {
  return (
    <div
      ref={composerSheetOverlayRef}
      className={`feed-screen__composer-sheet-overlay${open && !closing ? ' is-open' : ''}${closing ? ' is-closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={onClose}
      style={mobileSheetBackdropStyle}
    >
      <div
        ref={composerSheetRef}
        className={`feed-screen__composer-sheet${open && !closing ? ' is-open' : ''}${closing ? ' is-closing' : ''}`}
        onClick={(event) => event.stopPropagation()}
        style={mobileSheetStyle}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(440px 180px at 50% 0%, rgba(255,215,0,0.12), rgba(255,215,0,0) 72%), radial-gradient(320px 160px at 100% 0%, rgba(183,140,255,0.16), rgba(183,140,255,0) 62%)',
          }}
        />

        <div
          data-feed-sheet-drag-handle="1"
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '12px 14px 10px',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0))',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              paddingTop: 10,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minWidth: 0,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  background: '#FFD700',
                  boxShadow: '0 0 12px rgba(255,215,0,0.46)',
                  flex: '0 0 auto',
                  animation: 'none',
                }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  id={titleId}
                  style={{
                    color: '#FFF3C4',
                    fontSize: 15,
                    fontWeight: 900,
                    letterSpacing: 0,
                    lineHeight: 1.1,
                  }}
                >
                  {feedText('Crear publicación', 'Create post')}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.06)',
                color: '#F8F4FF',
                borderRadius: 999,
                padding: '8px 12px',
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.2,
                cursor: 'pointer',
                flexShrink: 0,
                boxShadow: '0 8px 18px rgba(0,0,0,0.12)',
              }}
            >
              {feedText('Cerrar', 'Close')}
            </button>
          </div>
        </div>

        <div
          data-feed-composer-scroll="1"
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '12px 12px 14px',
            maxHeight: '76vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <FeedComposer
            onSubmit={onSubmit}
            disabled={creatingPost}
            submitLabel={creatingPost ? feedText('Publicando...', 'Publishing...') : feedText('Publicar', 'Publish')}
            placeholder={feedText('¿Qué energía querés compartir hoy en Zodiacus?', 'What energy would you like to share on Zodiacus today?')}
          />
        </div>
      </div>
    </div>
  );
}

export default function FeedScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const topAnchorRef = useRef(null);
  const composerSectionRef = useRef(null);
  const recentPostRefs = useRef(new Map());
  const feedModeTabsRef = useRef(null);
  const { showToast } = useToast();

  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return 1280;
    return window.innerWidth || 1280;
  });

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = viewportWidth <= 640;
  const isTablet = viewportWidth <= 860;
  const [feedMode, setFeedMode] = useState('para-vos');
  const modeToastReadyRef = useRef(false);
  const viewerSnapshot = useMemo(() => getStoredViewerSnapshot(), []);
  const viewerSign = useMemo(
    () =>
      String(
        viewerSnapshot?.zodiacSign ||
          viewerSnapshot?.sign ||
          viewerSnapshot?.sunSign ||
          ''
      ).trim(),
    [viewerSnapshot]
  );

  const selectedThreadPostId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return String(params.get('post') || '').trim();
    } catch {
      return '';
    }
  }, [location.search]);

  const isThreadView = selectedThreadPostId !== '';

  const {
    items,
    loading,
    loadingMore,
    refreshing,
    creatingPost,
    error,
    hasMore,
    checkingNewPosts,
    recentlyAppliedPostIds,
    refresh,
    loadMore,
    submitPost,
    removePost,
    toggleLike,
  } = useFeedTimeline({
    initialLimit: 10,
    autoLoad: true,
    mode: feedMode,
  });

  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const feedModeOptions = useMemo(
    () => [
      {
        key: 'para-vos',
        label: feedText('Para vos', 'For you'),
        hint: feedText('Prioriza lo más fuerte del momento', 'Highlights the strongest activity right now'),
      },
      {
        key: 'recientes',
        label: feedText('Recientes', 'Recent'),
        hint: feedText('Orden cronológico visual', 'Shown in chronological order'),
      },
      {
        key: 'tu-signo',
        label: feedText('Tu signo', 'Your sign'),
        hint: viewerSign
          ? feedText(`Filtra publicaciones alineadas con ${viewerSign}`, `Filters posts aligned with ${viewerSign}`)
          : feedText('Curado para tu vibra zodiacal', 'Curated for your zodiac energy'),
      },
    ],
    [viewerSign]
  );

  const feedModeIndicatorStyle = useFeedModeIndicator({
    feedModeTabsRef,
    feedMode,
    optionsLength: feedModeOptions.length,
    enabled: true,
  });

  const cleanText = (text = '') => {
    return String(text)
      .split('\n')
      .filter(line => {
        const l = line.trim().toLowerCase();
        return !(
          l.startsWith('#seed:') ||
          l.startsWith('#seedcomment:') ||
          l.startsWith('#livefeedpost:') ||
          l.startsWith('#livefeedcomment:') ||
          l.startsWith('#liveforumthread:') ||
          l.startsWith('#liveforumreply:') ||
          l.startsWith('#seedthread') ||
          l.startsWith('#seedreply')
        );
      })
      .join('\n')
      .trim();
  };

  const visibleItems = useMemo(() => {
    const base = !selectedThreadPostId
      ? safeItems
      : safeItems.filter((item) => String(item?.id || '') === selectedThreadPostId);

    return base.map(item => ({
      ...item,
      text: cleanText(item?.text || ''),
      content: cleanText(item?.content || ''),
    }));
  }, [safeItems, selectedThreadPostId]);

  const mobileSheet = useMobileComposerSheet({
    isMobile,
    composerSectionRef,
  });

  useEffect(() => {
    if (!modeToastReadyRef.current) {
      modeToastReadyRef.current = true;
      return;
    }

    if (feedMode === 'para-vos') {
      showToast('Modo Para vos activado', { type: 'info', duration: 1400 });
      return;
    }

    if (feedMode === 'recientes') {
      showToast('Modo Recientes activado', { type: 'info', duration: 1400 });
      return;
    }

    if (feedMode === 'tu-signo') {
      showToast(
        viewerSign ? `Modo ${viewerSign} activado` : 'Modo Tu signo activado',
        { type: 'info', duration: 1400 }
      );
    }
  }, [feedMode, showToast, viewerSign]);

  const setRecentPostNode = useCallback((postId, node) => {
    const safePostId = String(postId || '');
    if (!safePostId) return;

    if (node) {
      recentPostRefs.current.set(safePostId, node);
    } else {
      recentPostRefs.current.delete(safePostId);
    }
  }, []);

  useEffect(() => {
    if (!recentlyAppliedPostIds.length) return;

    const firstRecentId = String(recentlyAppliedPostIds[0] || '');
    if (!firstRecentId) return;

    const timeout = window.setTimeout(() => {
      const targetNode = recentPostRefs.current.get(firstRecentId);

      if (targetNode?.scrollIntoView) {
        targetNode.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        return;
      }

      if (topAnchorRef.current?.scrollIntoView) {
        topAnchorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 120);

    return () => window.clearTimeout(timeout);
  }, [recentlyAppliedPostIds]);

  const handleSubmitPost = useCallback(
  async ({ text, uploadId = null, upload = null, rawText = '' }) => {
    try {
      const result = await submitPost({ text, uploadId, upload, rawText });

      if (isMobile) {
        mobileSheet.closeMobileComposer();
      }

      showToast(feedText('Publicación enviada', 'Post published'), { type: 'success' });
      return result;
    } catch (err) {
      showToast(err?.message || feedText('No se pudo publicar', 'The post could not be published'), { type: 'error' });
      throw err;
    }
  },
  [isMobile, mobileSheet, showToast, submitPost]
);

  const handleDeletePost = useCallback(
    async (post) => {
      const postId = String(post?.id || '');
      if (!postId) return null;

      try {
        const result = await removePost(postId);
        return result;
      } catch (err) {
        showToast(err?.message || feedText('No se pudo eliminar', 'The post could not be deleted'), { type: 'error' });
        throw err;
      }
    },
    [removePost, showToast]
  );

  const handleLikePost = useCallback(async (post) => toggleLike(post), [toggleLike]);

  const handleRefresh = useCallback(async () => {
    if (loading || loadingMore || refreshing) return;

    try {
      await refresh();
      showToast(feedText('Feed actualizado', 'Feed updated'), { type: 'success', duration: 1800 });
    } catch (err) {
      showToast(err?.message || feedText('No se pudo actualizar', 'The feed could not be updated'), { type: 'error' });
    }
  }, [refresh, loading, loadingMore, refreshing, showToast]);

  const handleCloseThread = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: '',
    });
  }, [location.pathname, navigate]);

  const handleBackHome = useCallback(() => {
    const token = getAuthToken();
    navigate(token ? '/home' : '/welcome', { replace: true });
  }, [navigate]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    try {
      await loadMore();
    } catch (err) {
      showToast(err?.message || feedText('No se pudieron cargar más publicaciones', 'More posts could not be loaded'), {
        type: 'error',
      });
    }
  }, [hasMore, loadMore, loadingMore, showToast]);

  const handleOpenThread = useCallback(
    (post) => {
      const postId = String(post?.id || '').trim();
      if (!postId) return;

      const params = new URLSearchParams(location.search || '');
      params.set('post', postId);

      navigate({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
    },
    [location.pathname, location.search, navigate]
  );

  const loadMoreSentinelRef = useFeedInfiniteScroll({
    disabled:
      loading ||
      loadingMore ||
      refreshing ||
      !hasMore ||
      visibleItems.length === 0,
    onLoadMore: handleLoadMore,
  });

  return (
    <section
      className={`feed-screen${isMobile ? ' feed-screen--mobile' : ''}${!isMobile && isTablet ? ' feed-screen--tablet' : ''}`}
      ref={topAnchorRef}
    >
      <div className={`feed-screen__container${isMobile ? ' feed-screen__container--mobile' : ''}`}>
        <header
          className="feed-screen__hero"
          style={{
            padding: isMobile ? '10px 10px 8px' : '14px 16px 12px',
            borderRadius: isMobile ? 18 : 24,
            background: isMobile
              ? 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.028))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.04))',
            border: isMobile
              ? '1px solid rgba(255,224,130,0.12)'
              : '1px solid rgba(255,224,130,0.16)',
            boxShadow: isMobile
              ? '0 12px 30px rgba(8,5,20,0.18), 0 0 0 1px rgba(255,255,255,0.02) inset'
              : '0 20px 44px rgba(8,5,20,0.22), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 28px rgba(255,224,130,0.05)',
            position: 'relative',
            overflow: 'hidden',
            marginBottom: isMobile ? 10 : 12,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'radial-gradient(420px 180px at 0% 0%, rgba(255,215,0,0.12), rgba(255,215,0,0) 60%), radial-gradient(360px 180px at 100% 0%, rgba(183,140,255,0.16), rgba(183,140,255,0) 62%)',
            }}
          />

          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 10 : 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 11px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,224,130,0.10)',
                    color: 'rgba(255,243,196,0.92)',
                    fontSize: isMobile ? 11 : 11,
                    fontWeight: 900,
                    letterSpacing: 0.18,
                    marginBottom: 8,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: refreshing || checkingNewPosts || loadingMore ? '#FFD700' : '#B78CFF',
                      boxShadow: refreshing || checkingNewPosts || loadingMore
                        ? '0 0 10px rgba(255,215,0,0.5)'
                        : '0 0 10px rgba(183,140,255,0.42)',
                      animation: refreshing || checkingNewPosts || loadingMore
                        ? 'feed-live-dot-pulse 1.25s ease-in-out infinite'
                        : 'none',
                    }}
                  />
                  Feed Zodiacus
                </div>

              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: isMobile ? 'stretch' : 'flex-end',
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (isThreadView) {
                      handleCloseThread();
                      return;
                    }
                    handleBackHome();
                  }}
                  className="feed-screen__toolbar-button"
                  style={{
                    minWidth: isMobile ? 0 : 124,
                    flex: isMobile ? '1 1 0' : '0 0 auto',
                    padding: isMobile ? '10px 12px' : '10px 14px',
                    borderRadius: 15,
                    background: 'linear-gradient(135deg, rgba(255,224,130,0.16), rgba(255,255,255,0.06))',
                    color: '#FFF0BE',
                    border: '1px solid rgba(255,224,130,0.16)',
                    boxShadow: '0 10px 24px rgba(0,0,0,0.14)',
                  }}
                >
                  {isThreadView
                    ? (isMobile ? '← Feed' : feedText('← Volver al feed', '← Back to feed'))
                    : (isMobile ? feedText('← Inicio', '← Home') : feedText('← Volver al inicio', '← Back to home'))}
                </button>

                <button
                  type="button"
                  onClick={handleRefresh}
                  className="feed-screen__toolbar-button feed-screen__primary-button"
                  disabled={loading || loadingMore || refreshing}
                  style={{
                    minWidth: isMobile ? 0 : 136,
                    flex: isMobile ? '1 1 0' : '0 0 auto',
                    padding: isMobile ? '10px 12px' : '10px 14px',
                    borderRadius: 15,
                    opacity: loading || loadingMore || refreshing ? 0.82 : 1,
                  }}
                >
                  {loading || loadingMore || refreshing ? feedText('Actualizando...', 'Refreshing...') : (isMobile ? feedText('Actualizar', 'Refresh') : feedText('Actualizar feed', 'Refresh feed'))}
                </button>
              </div>
            </div>
          </div>
        </header>
        {!isThreadView ? (
          <div
            className="feed-screen__sticky-stack"
            style={{
              position: isMobile ? 'relative' : 'sticky',
              top: isMobile ? 'auto' : 12,
              zIndex: isMobile ? 0 : 18,
              gap: isMobile ? 8 : 12,
              paddingTop: isMobile ? 2 : 0,
              marginBottom: isMobile ? 4 : 0,
            }}
          >
            <FeedModeBar
              feedMode={feedMode}
              setFeedMode={setFeedMode}
              feedModeOptions={feedModeOptions}
              feedModeTabsRef={feedModeTabsRef}
              feedModeIndicatorStyle={feedModeIndicatorStyle}
              isMobile={isMobile}
              refreshing={refreshing}
              checkingNewPosts={checkingNewPosts}
            />
          </div>
        ) : null}

        {!isThreadView ? (
          <div
            ref={composerSectionRef}
            className="feed-screen__panel feed-screen__composer-trigger"
            style={{
              padding: isMobile ? '10px 12px' : '12px 14px',
              borderRadius: isMobile ? 16 : 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              position: 'relative',
              zIndex: isMobile ? 0 : 1,
              background: isMobile
                ? 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03))'
                : 'linear-gradient(180deg, rgba(255,255,255,0.085), rgba(255,255,255,0.038))',
              boxShadow: isMobile
                ? '0 10px 22px rgba(8,5,20,0.14), 0 0 0 1px rgba(255,255,255,0.03) inset'
                : '0 18px 42px rgba(8,5,20,0.22), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 26px rgba(255,224,130,0.05)',
              marginTop: isMobile ? 4 : 0,
              overflow: 'hidden',
              border: isMobile
                ? '1px solid rgba(255,224,130,0.12)'
                : '1px solid rgba(255,224,130,0.14)',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background:
                  'linear-gradient(120deg, rgba(255,215,0,0.10), rgba(255,255,255,0.02) 28%, rgba(183,140,255,0.10) 60%, rgba(255,215,0,0.06))',
                opacity: mobileSheet.mobileComposerOpen || mobileSheet.mobileComposerMounted ? 1 : 0.72,
                transition: 'opacity 180ms ease',
              }}
            />
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '-42%',
                width: '42%',
                pointerEvents: 'none',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.16), transparent)',
                transform:
                  mobileSheet.mobileComposerOpen || mobileSheet.mobileComposerMounted
                    ? 'translateX(265%)'
                    : 'translateX(0%)',
                transition: 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
            <div
              style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minWidth: 0,
                flex: 1,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: '#FFD700',
                  boxShadow: '0 0 10px rgba(255,215,0,0.42)',
                  flex: '0 0 auto',
                }}
              />
              <span
                style={{
                  color: 'rgba(248,244,255,0.84)',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 800,
                  letterSpacing: 0.1,
                  lineHeight: 1.3,
                }}
              >
                {mobileSheet.mobileComposerOpen
                  ? feedText('Nuevo post listo para publicar.', 'New post ready to publish.')
                  : feedText('Compartí algo con la comunidad.', 'Share something with the community.')}
              </span>
            </div>

            <button
              type="button"
              className="feed-screen__toolbar-button feed-screen__primary-button"
              onClick={mobileSheet.handleToggleMobileComposer}
              style={{
                position: 'relative',
                zIndex: 1,
                minWidth: isMobile ? 'auto' : 132,
                padding: isMobile ? '7px 11px' : '9px 14px',
                fontSize: isMobile ? 10.5 : 12,
                justifyContent: 'center',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                boxShadow: isMobile
                  ? '0 6px 14px rgba(0,0,0,0.10)'
                  : '0 12px 24px rgba(0,0,0,0.18), 0 0 20px rgba(255,224,130,0.08)',
              }}
            >
              {mobileSheet.mobileComposerOpen ? 'Ocultar editor' : 'Crear post'}
            </button>
          </div>
        ) : null}

        {!isThreadView && (isMobile ? mobileSheet.mobileComposerMounted : mobileSheet.mobileComposerOpen) ? (
          isMobile ? (
            <FeedMobileSheet
              open={mobileSheet.mobileComposerOpen}
              closing={mobileSheet.mobileComposerClosing}
              titleId={mobileSheet.mobileSheetTitleId}
              descriptionId={mobileSheet.mobileSheetDescriptionId}
              onClose={mobileSheet.closeMobileComposer}
              onSubmit={handleSubmitPost}
              creatingPost={creatingPost}
              composerSheetRef={mobileSheet.composerSheetRef}
              composerSheetOverlayRef={mobileSheet.composerSheetOverlayRef}
              composerSheetHandleRef={mobileSheet.composerSheetHandleRef}
            />
          ) : (
            <div className="feed-screen__panel feed-screen__composer">
              <FeedComposer
                onSubmit={handleSubmitPost}
                disabled={creatingPost}
                submitLabel={creatingPost ? feedText('Publicando...', 'Publishing...') : feedText('Publicar', 'Publish')}
                placeholder={feedText('¿Qué energía querés compartir hoy en Zodiacus?', 'What energy would you like to share on Zodiacus today?')}
              />
            </div>
          )
        ) : null}

        {error ? (
          <div
            className="feed-screen__panel feed-screen__error"
            role="alert"
            style={{
              color: '#FFE1E1',
              border: '1px solid rgba(255,110,110,0.28)',
              background:
                'linear-gradient(180deg, rgba(255,88,88,0.08), rgba(255,255,255,0.03))',
            }}
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="feed-screen__panel">
            <FeedSkeletonList count={3} />
          </div>
        ) : null}

        {!loading && visibleItems.length === 0 ? (
          <div
            className="feed-screen__panel"
            style={{
              padding: isMobile ? '8px 10px' : '10px 12px',
              borderRadius: isMobile ? 14 : 22,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 'auto',
              background: isMobile
                ? 'rgba(255,255,255,0.035)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.085), rgba(255,255,255,0.038))',
              boxShadow: isMobile
                ? '0 8px 18px rgba(8,5,20,0.10)'
                : '0 18px 42px rgba(8,5,20,0.22), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 26px rgba(255,224,130,0.05)',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: '#FFD700',
                boxShadow: '0 0 10px rgba(255,215,0,0.42)',
                flex: '0 0 auto',
              }}
            />
            <span
              style={{
                color: 'rgba(248,244,255,0.84)',
                fontSize: isMobile ? 12 : 13,
                fontWeight: 800,
                letterSpacing: 0.1,
                lineHeight: 1.3,
              }}
            >
              {feedText('Todavía no hay publicaciones.', 'There are no posts yet.')}
            </span>
          </div>
        ) : null}

        {!loading && visibleItems.length > 0 ? (
          <div
            className="feed-screen__list"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? 12 : 16,
              position: 'relative',
            }}
          >
            {visibleItems.map((post) => {
              const postId = String(post?.id || '');

              return (
                <div
                  key={postId || Math.random()}
                  ref={(node) => setRecentPostNode(postId, node)}
                >
                  <FeedPostCard
  post={post}
  onLike={handleLikePost}
  onDelete={handleDeletePost}
  onOpenThread={handleOpenThread}
  showInlineComments={isThreadView}
  initiallyExpanded={isThreadView}
  isThreadView={isThreadView}
  deepLinkedPostId={selectedThreadPostId}
  isRecentlyApplied={recentlyAppliedPostIds.includes(postId)}
/>
                </div>
              );
            })}
          </div>
        ) : null}

        {!loading && visibleItems.length > 0 && !isThreadView ? (
          <div
            className="feed-screen__footer"
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: 2,
            }}
          >
            {hasMore ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <button
                  type="button"
                  className="feed-screen__load-more feed-screen__toolbar-button feed-screen__primary-button"
                  style={{
                    minWidth: 220,
                    justifyContent: 'center',
                    width: isMobile ? '100%' : 'auto',
                  }}
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    {loadingMore ? (
                      <span
                        aria-hidden="true"
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 999,
                          background: '#FFD700',
                          boxShadow: '0 0 10px rgba(255,215,0,0.50)',
                          animation: 'feed-live-dot-pulse 1.25s ease-in-out infinite',
                        }}
                      />
                    ) : null}
                    {loadingMore ? feedText('Cargando más...', 'Loading more...') : feedText('Ver más publicaciones', 'View more posts')}
                  </span>
                </button>
                <div
                  ref={loadMoreSentinelRef}
                  aria-hidden="true"
                  style={{
                    width: '100%',
                    height: 2,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                />

                <div
                  style={{
                    color: 'rgba(245,240,255,0.58)',
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: 0.12,
                  }}
                >
                  {feedText('Deslizá para seguir explorando automáticamente.', 'Swipe to keep exploring automatically.')}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
